import { ChangeDetectionStrategy, Component, ContentChild, OnInit, Signal, WritableSignal, computed, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, filter, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { MatFormField, MatInput, MatLabel, MatPrefix } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { IsLoadingByIdPipe } from '../../pipes';
import { DataTableColumn, DataTableModule } from '../../components';
import { BuyerPersonasNoDataDirective } from './buyer-personas-no-data.directive';
import {
  BuyerPersonaListItemDto,
  HubspotBuyerPersonasListRequestDto,
  HubspotBuyerPersonasListResponseDto,
} from '../../dto';
import { BuyerPersonasApiService } from '../../services/api';
import { LoadingService } from '../../services';
import { Sort } from '@angular/material/sort';
import { IsLoading, LoadingWrapper } from '../../decorators';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CreateEditBuyerPersonaGroupDialogComponent } from '../create-edit-buyer-persona-group/create-edit-buyer-persona-group-dialog.component';
import { ConfirmationDeleteComponent } from './components/confirmation-delete/confirmation-delete.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BuyerPersonasActionTooltipPipe, BuyingPersonasDisplayQueryPipe } from './pipes';
import { BuyerPersonasClampComponent } from './components/buyer-personas-clamp/buyer-personas-clamp.component';

const LOADING_TAG: string = 'BUYING_PERSONAS_GROUP_LOADING_TAG';
const DELETE_LOADING_TAG: string = 'DELETE_BUYING_PERSONAS_GROUP_LOADING_TAG';

@UntilDestroy()
@Component({
  selector: 'app-buyer-personas',
  templateUrl: './buyer-personas.component.html',
  imports: [
    DataTableModule,
    InfiniteScrollDirective,
    MatFormField,
    MatButton,
    AsyncPipe,
    MatTooltip,
    FontAwesomeModule,
    MatIconButton,
    IsLoadingByIdPipe,
    MatProgressSpinner,
    MatLabel,
    ReactiveFormsModule,
    MatInput,
    MatPrefix,
    BuyingPersonasDisplayQueryPipe,
    BuyerPersonasActionTooltipPipe,
    BuyerPersonasClampComponent,
    NgFor,
    NgIf,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyerPersonasComponent implements OnInit {
  @IsLoading(LOADING_TAG) isLoading$: Observable<boolean>;

  readonly filterLabel = input<string>('Filter by name…');
  readonly filterPlaceholder = input<string>('Filter by name…');
  readonly hideAddNewButton = input<boolean>(false);
  readonly isOpsAdminOrAdmin = input<boolean>(false);
  // Non-admins get the "Ask your admin for assistance" hint on the disabled Add New button;
  // admins get no tooltip. Derived here so the template stays free of conditional logic.
  readonly newTooltip: Signal<string> = computed<string>(() =>
    this.isOpsAdminOrAdmin() ? '' : 'Ask your admin for assistance');
  @ContentChild(BuyerPersonasNoDataDirective, { static: false }) noDataTemplate: BuyerPersonasNoDataDirective;

  buyerPersonaList: BuyerPersonaListItemDto[] = [];
  searchControl: FormControl = new FormControl();
  isLoadingMore$: Observable<boolean>;
  hasLoadedOnce: WritableSignal<boolean> = signal<boolean>(false);
  hasAnyData: WritableSignal<boolean> = signal<boolean>(false);

  readonly DELETE_LOADING_TAG: string = DELETE_LOADING_TAG;
  readonly defaultParams: HubspotBuyerPersonasListRequestDto = {
    filterName: "",
    pageSize: 250,
    pageNumber: 1,
    sortBy: "modifiedDate",
    sortDirection: "desc"
  }
  readonly columns: DataTableColumn[] = [
    {
      dataField: "name",
      cellTemplate: "nameTpl",
    },
    {
      dataField: "modifiedDate",
      caption: "Last Modified Date",
      cellTemplate: "dateTemplate",
    },
    {
      dataField: "selections",
      cellTemplate: "selectionsTpl",
      caption: 'Attributes',
      allowSorting: false,
    },
    {
      dataField: "actions",
      caption: "",
      width: "120px",
      cellTemplate: "actionsTpl",
      allowSorting: false,
    }
  ];

  private totalCount: number = 0;
  private readonly isLoadingMoreSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly searchSubject: BehaviorSubject<HubspotBuyerPersonasListRequestDto> = new BehaviorSubject(this.defaultParams);

  constructor(
    private readonly buyerPersonasApiService: BuyerPersonasApiService,
    private readonly loadingService: LoadingService,
    private readonly matDialog: MatDialog,
    private readonly toastrService: ToastrService,
  ) {
  }

  ngOnInit(): void {
    this.isLoadingMore$ = this.isLoadingMoreSubject.asObservable();

    this.searchSubject.asObservable().pipe(
      debounceTime(500),
      switchMap((request) => this.getBuyerPersonasList(request)),
      untilDestroyed(this),
    ).subscribe((result: HubspotBuyerPersonasListResponseDto) => {
      if (!this.hasLoadedOnce()) {
        this.hasLoadedOnce.set(true);
      }
      if (!this.hasAnyData() && result.totalCount > 0) {
        this.hasAnyData.set(true);
      }
      if (result.pageNumber === 1) {
        this.buyerPersonaList = [...result.buyerPersonaList];
      } else {
        this.buyerPersonaList = [...this.buyerPersonaList, ...result.buyerPersonaList];
      }
      this.totalCount = result.totalCount;
      this.isLoadingMoreSubject.next(false);
    });

    this.searchControl.valueChanges.pipe(
      untilDestroyed(this),
    ).subscribe((filterName: string) => {
      this.searchSubject.next({
        ...this.searchSubject.getValue(),
        pageNumber: 1,
        filterName,
      })
    })
  }

  onEditCreateBuyerPersona(row?): void {
    this.matDialog.open(CreateEditBuyerPersonaGroupDialogComponent, {
      panelClass: ['modal-lg', 'modal-dialog--fixed-height'],
      data: row,
    }).afterClosed().pipe(filter(Boolean)).subscribe(() => {
      this.refreshList();
    });
  }

  onSortingChanged(event: Sort): void {
    this.searchSubject.next({
      ...this.searchSubject.getValue(),
      pageNumber: 1,
      sortBy: event.active,
      sortDirection: event.direction,
    })
  }

  onLoadMore(): void {
    this.isLoadingMore$.pipe(
      filter((isLoading: boolean) => !isLoading),
      take(1),
    ).subscribe(() => {
      const search: HubspotBuyerPersonasListRequestDto = this.searchSubject.getValue();

      if (this.buyerPersonaList?.length < this.totalCount) {
        this.searchSubject.next({
          ...search,
          pageNumber: search.pageNumber + 1,
        });
        this.isLoadingMoreSubject.next(true);
      }
    })
  }

  onDelete(rowId: string, index: number): void {
    this.matDialog.open(ConfirmationDeleteComponent).afterClosed()
    .pipe(
      filter(Boolean),
      switchMap(() => this.deleteBuyerPersona(rowId)),
      untilDestroyed(this)
    )
    .subscribe(() => {
      this.buyerPersonaList.splice(index, 1);
      this.buyerPersonaList = [...this.buyerPersonaList];
      this.toastrService.success('Buyer persona group successfully deleted.');
    })
  }

  private refreshList(): void {
    this.searchSubject.next({
      ...this.searchSubject.getValue(),
      pageNumber: 1,
    })
  }

  @LoadingWrapper(LOADING_TAG)
  private getBuyerPersonasList(request: HubspotBuyerPersonasListRequestDto): Observable<HubspotBuyerPersonasListResponseDto> {
    return this.buyerPersonasApiService.getBuyerPersonasList(request);
  }

  private deleteBuyerPersona(id: string): Observable<void> {
    return this.loadingService.loadingWrapper(this.buyerPersonasApiService.deleteBuyerPersona(id), `${DELETE_LOADING_TAG}-${id}`)
  }
}
